import React, { ComponentPropsWithoutRef, ElementType } from 'react';
import { motion, MotionProps, useReducedMotion } from 'framer-motion';

export type RevealDirection = 'up' | 'down' | 'left' | 'right' | 'scale';

type PolymorphicProps<E extends ElementType> = Omit<ComponentPropsWithoutRef<E>, 'as' | 'children'>;

export type RevealProps<E extends ElementType = 'div'> = {
  as?: E;
  className?: string;
  delay?: number;
  duration?: number;
  direction?: RevealDirection;
  distance?: number;
  staggerIndex?: number;
  viewportAmount?: number;
  once?: boolean;
  motionProps?: MotionProps;
  children?: React.ReactNode;
} & PolymorphicProps<E>;

const directionMap: Record<RevealDirection, { x?: number; y?: number; scale?: number }> = {
  up: { y: 1 },
  down: { y: -1 },
  left: { x: 1 },
  right: { x: -1 },
  scale: { scale: 0.94 },
};

const DEFAULT_DISTANCE = 32;

const Reveal = <E extends ElementType = 'div'>(
  {
    as: Component = 'div' as E,
    children,
    className,
    delay = 0,
    duration = 0.6,
    direction = 'up',
    distance = DEFAULT_DISTANCE,
    motionProps,
    once = true,
    staggerIndex = 0,
    viewportAmount = 0.35,
    ...rest
  }: RevealProps<E>,
) => {
  const shouldReduceMotion = useReducedMotion();
  const offset = directionMap[direction] ?? directionMap.up;

  const initial = shouldReduceMotion
    ? { opacity: 0 }
    : {
        opacity: 0,
        ...(offset.x ? { x: offset.x * distance } : {}),
        ...(offset.y ? { y: offset.y * distance } : {}),
        ...(offset.scale ? { scale: offset.scale } : {}),
      };

  const animate = shouldReduceMotion
    ? { opacity: 1 }
    : {
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
        transition: {
          duration,
          delay: delay + staggerIndex * 0.08,
          ease: [0.22, 1, 0.36, 1],
        },
      };

  const MotionComponent = motion(Component as ElementType);

  const shouldUseViewport = !once;

  return (
    <MotionComponent
      className={className}
      initial="hidden"
      animate="visible"
      whileInView={motionProps?.whileInView ?? (shouldUseViewport ? 'visible' : undefined)}
      viewport={motionProps?.viewport ?? (shouldUseViewport ? { once, amount: viewportAmount } : undefined)}
      variants={{ hidden: initial, visible: animate }}
      {...rest}
      {...motionProps}
    >
      {children}
    </MotionComponent>
  );
};

export default Reveal;
